import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";

import { streamNarrative } from "../api/client";

/**
 * NarrativeBrief — pro-ai-analyst Task 10.
 *
 * Streams the Pro AI Analyst's opening intelligence brief below the instant
 * score. While the analyst investigates, it shows the investigation steps it's
 * taking; then it renders the brief. On `done` it hands the analyst's
 * property-specific follow-up questions up to the parent so the chat's starter
 * chips become dynamic (grounded in THIS property, not a fixed set).
 *
 * Reconnect (Req 6.6): when the score was loaded via GET /score after the
 * narrative already finished, `initialNarrative` is non-empty — we render it
 * directly and never re-run the analyst.
 *
 * Transport is buffered today (issue #24 tracks true token-by-token reveal),
 * so the steps + brief arrive together; the "Investigating…" state covers the
 * wait, matching the chat panel's buffered UX.
 */

interface Props {
  scoreId: string;
  /** `narrative_url` from the score response; falls back to VITE_NARRATIVE_URL. */
  narrativeUrl?: string;
  /** Persisted narrative from GET /score — non-empty only after a reload. */
  initialNarrative?: string;
  /** Receives the analyst's suggested follow-up questions on completion. */
  onSuggestions?: (questions: string[]) => void;
}

type Status = "investigating" | "streaming" | "done" | "error";

export function NarrativeBrief({
  scoreId,
  narrativeUrl,
  initialNarrative,
  onSuggestions,
}: Props) {
  const hasPersisted = Boolean(initialNarrative && initialNarrative.trim());
  const [text, setText] = useState(initialNarrative ?? "");
  const [steps, setSteps] = useState<string[]>([]);
  const [status, setStatus] = useState<Status>(hasPersisted ? "done" : "investigating");
  const [error, setError] = useState<string | null>(null);

  // Keep the latest callback without retriggering the stream effect.
  const onSuggestionsRef = useRef(onSuggestions);
  onSuggestionsRef.current = onSuggestions;

  useEffect(() => {
    // Reload path: the brief already streamed and was persisted; show it.
    if (hasPersisted) return;

    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        for await (const ev of streamNarrative(scoreId, controller.signal, narrativeUrl)) {
          if (cancelled) return;
          if (ev.event === "investigation_step") {
            setSteps((prev) => [...prev, ev.data.step]);
          } else if (ev.event === "token") {
            setStatus("streaming");
            setText((prev) => prev + ev.data);
          } else if (ev.event === "done") {
            setStatus("done");
            const qs = ev.data.suggested_questions;
            if (qs && qs.length) onSuggestionsRef.current?.(qs);
          } else if (ev.event === "error") {
            setStatus("error");
            setError(ev.data.message);
          }
        }
      } catch (e) {
        if (cancelled) return; // an AbortError from unmount is expected
        setStatus("error");
        setError(e instanceof Error ? e.message : "Analysis unavailable.");
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [scoreId, narrativeUrl, hasPersisted]);

  return (
    <section className="pro-narrative" aria-label="What the data shows">
      <h3>What the data shows</h3>

      {/* Investigation steps — visible while the analyst works; hidden once the
          finished brief is on screen. */}
      {status !== "done" && steps.length > 0 && (
        <ul className="narrative-steps" aria-live="polite">
          {steps.map((s, i) => (
            <li key={i} className="narrative-step">
              {s}…
            </li>
          ))}
        </ul>
      )}

      {text ? (
        <div className="chat-markdown">
          <ReactMarkdown>{text}</ReactMarkdown>
        </div>
      ) : status === "error" ? (
        <p className="narrative-error" role="alert">
          {error ?? "Analysis unavailable."} Reload the page to retry.
        </p>
      ) : (
        <div className="pro-narrative-loading" aria-live="polite">
          {steps.length === 0 && (
            <p className="narrative-status">Investigating this property…</p>
          )}
        </div>
      )}
    </section>
  );
}
